// src/pages/ResetPassword/ResetPassword.tsx
// Route: /reset-password/:token
import React, { useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { postRequest } from "../../services/api";
import "../LoginPage/LoginPage.css";
import "./ResetPassword.css";

/* ── Password strength helper ── */
type StrengthLevel = "none" | "weak" | "fair" | "strong";

function getStrength(pwd: string): StrengthLevel {
  if (pwd.length === 0) return "none";
  if (pwd.length < 4)   return "weak";
  if (pwd.length < 8)   return "fair";
  if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{8,}$/.test(pwd)) return "fair";
  return "strong";
}

const STRENGTH_LABELS: Record<StrengthLevel, string> = {
  none:   "",
  weak:   "Weak",
  fair:   "Fair",
  strong: "Strong",
};

const ResetPassword: React.FC = () => {
  const { token }   = useParams<{ token: string }>();
  const navigate    = useNavigate();

  const [password,  setPassword]  = useState("");
  const [confirm,   setConfirm]   = useState("");
  const [showPwd,   setShowPwd]   = useState(false);
  const [showConf,  setShowConf]  = useState(false);
  const [errors,    setErrors]    = useState<{ password?: string; confirm?: string }>({});
  const [loading,   setLoading]   = useState(false);
  const [done,      setDone]      = useState(false);
  const [apiError,  setApiError]  = useState("");

  const strength = getStrength(password);

  /* ── Validate ── */
  const validate = (): boolean => {
    const e: { password?: string; confirm?: string } = {};
    if (!/^(?=.*[A-Za-z])(?=.*\d)[A-Za-z\d@$!%*#?&]{6,}$/.test(password)) {
      e.password = "Password must be at least 6 characters and contain letters and numbers";
    }
    if (confirm !== password) {
      e.confirm = "Passwords do not match";
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  /* ── Submit ── */
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;
    if (!token) { setApiError("Invalid or missing reset token."); return; }

    setLoading(true);
    setApiError("");
    try {
      await postRequest(`/auth/reset-password/${token}`, { password });
      setDone(true);
      // Redirect to login after 3 seconds
      setTimeout(() => navigate("/login"), 3000);
    } catch (err: any) {
      const msg: string =
        err?.response?.data?.message ||
        err?.message ||
        "Something went wrong. The link may have expired.";
      setApiError(msg);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">

      {/* ── Left panel ── */}
      <div className="auth-left rp-left">
        <div className="auth-left-inner">
          <Link to="/" className="auth-logo">
            <span className="auth-logo-mark">A</span>
            <span className="auth-logo-text">cadebility</span>
          </Link>

          <div className="auth-headline">
            <h1>Set a new<br /><span className="auth-accent">strong password.</span></h1>
            <p className="auth-tagline">
              Choose a password you haven't used before. A strong password keeps your account safe.
            </p>
          </div>

          <div className="auth-features">
            {[
              { icon: "🔡", title: "At least 6 characters", desc: "The longer, the better" },
              { icon: "🔢", title: "Mix letters & numbers",  desc: "E.g. Rohan2024 is stronger than rohan" },
              { icon: "🚫", title: "Avoid obvious choices",  desc: "Don't use your name, birthday or '123456'" },
              { icon: "✅", title: "Confirm it twice",       desc: "Both fields must match exactly" },
            ].map(p => (
              <div className="auth-feat-item" key={p.title}>
                <span className="auth-feat-icon">{p.icon}</span>
                <div>
                  <div className="auth-feat-title">{p.title}</div>
                  <div className="auth-feat-desc">{p.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
        <div className="auth-blob auth-blob-1" />
        <div className="auth-blob auth-blob-2" />
      </div>

      {/* ── Right panel ── */}
      <div className="auth-right">
        <div className="auth-form-card rp-card">

          {!done ? (
            <>
              <div className="auth-form-header">
                <div className="rp-icon-wrap">
                  <svg viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 2l-2 2m-7.61 7.61a5.5 5.5 0 1 1-7.778 7.778 5.5 5.5 0 0 1 7.777-7.777zm0 0L15.5 7.5m0 0l3 3L22 7l-3-3m-3.5 3.5L19 4"/>
                  </svg>
                </div>
                <h2 className="auth-form-title">Create new password</h2>
                <p className="auth-form-sub">
                  Your new password must be different from previously used passwords.
                </p>
              </div>

              {apiError && (
                <div className="auth-alert auth-alert-error">
                  <span>⚠️</span> {apiError}
                </div>
              )}

              <form className="auth-form" onSubmit={handleSubmit} noValidate>

                {/* New password */}
                <div className="auth-field">
                  <label className="auth-label">New password</label>
                  <div className="auth-input-wrap">
                    <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/>
                      <path d="M7 11V7a5 5 0 0 1 10 0v4"/>
                    </svg>
                    <input
                      type={showPwd ? "text" : "password"}
                      name="password"
                      className={`auth-input${errors.password ? " auth-input-err" : ""}`}
                      placeholder="At least 6 characters"
                      value={password}
                      onChange={e => {
                        setPassword(e.target.value);
                        if (errors.password) setErrors(prev => ({ ...prev, password: "" }));
                      }}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="auth-pwd-toggle"
                      onClick={() => setShowPwd(p => !p)}
                      tabIndex={-1}
                      aria-label={showPwd ? "Hide password" : "Show password"}
                    >
                      {showPwd ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.password && <span className="auth-err">{errors.password}</span>}

                  {/* Password strength bar */}
                  <div className="reg-strength-bar">
                    <div className={`reg-strength-fill reg-str-${strength}`} />
                  </div>
                  {strength !== "none" && (
                    <span className={`rp-strength-label rp-str-${strength}`}>
                      {STRENGTH_LABELS[strength]}
                    </span>
                  )}
                </div>

                {/* Confirm password */}
                <div className="auth-field">
                  <label className="auth-label">Confirm new password</label>
                  <div className="auth-input-wrap">
                    <svg className="auth-input-icon" viewBox="0 0 24 24" fill="none"
                      stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"/>
                      <polyline points="22 4 12 14.01 9 11.01"/>
                    </svg>
                    <input
                      type={showConf ? "text" : "password"}
                      name="confirm"
                      className={`auth-input${errors.confirm ? " auth-input-err" : ""}`}
                      placeholder="Re-enter your new password"
                      value={confirm}
                      onChange={e => {
                        setConfirm(e.target.value);
                        if (errors.confirm) setErrors(prev => ({ ...prev, confirm: "" }));
                      }}
                      required
                      autoComplete="new-password"
                    />
                    <button
                      type="button"
                      className="auth-pwd-toggle"
                      onClick={() => setShowConf(p => !p)}
                      tabIndex={-1}
                      aria-label={showConf ? "Hide password" : "Show password"}
                    >
                      {showConf ? (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24"/>
                          <line x1="1" y1="1" x2="23" y2="23"/>
                        </svg>
                      ) : (
                        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/>
                          <circle cx="12" cy="12" r="3"/>
                        </svg>
                      )}
                    </button>
                  </div>
                  {errors.confirm && <span className="auth-err">{errors.confirm}</span>}

                  {/* Match indicator — only show once user has started typing confirm */}
                  {confirm.length > 0 && !errors.confirm && password === confirm && (
                    <span className="rp-match-ok">✓ Passwords match</span>
                  )}
                </div>

                <button type="submit" className="auth-btn-primary" disabled={loading}>
                  {loading
                    ? <><span className="auth-spinner" /> Updating password…</>
                    : "Update password →"
                  }
                </button>
              </form>
            </>
          ) : (
            /* ── Success state ── */
            <div className="rp-success">
              <div className="rp-success-icon">🎉</div>
              <h2 className="rp-success-title">Password updated!</h2>
              <p className="rp-success-msg">
                Your password has been reset successfully. You'll be redirected to
                the login page in a moment.
              </p>
              <Link to="/login" className="auth-btn-primary rp-login-link">
                Go to Login →
              </Link>
            </div>
          )}

          <p className="auth-switch">
            Back to{" "}
            <Link to="/login" className="auth-switch-link">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
};

export default ResetPassword;